import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import {ANALYSIS_TOOL_SPACING} from 'analysis-tool/AnalysisToolConstants';
import getDownstreamSteps from 'analysis-tool/data/getDownstreamSteps';
import {UNSET_CONFIG_VARIABLE} from 'analysis-tool/templates/AnalysisTemplateDAO';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import DashboardLayout, {BlockSpec} from './DashboardLayout';
import DashboardVisualizationBlock from './DashboardVisualizationBlock';
import DataFrameFilterDropdown, {getInitialFilterDropdownValue} from './DataFrameFilterDropdown';
import LegacyParameterOptimizationModelingDashboardView from './legacy-components/LegacyParameterOptimizationModelingDashboardView';

const BLOCK_HEIGHT = 400;
const BASE_BLOCK_SPEC: Omit<BlockSpec, 'key' | 'block'> = {
  // We need to set the min height of our blocks in block spec to give it an initial height and max height to
  // restrict the height of blocks that could otherwise be tall because of uncontrolled height like blocks containing
  // `DataFrameTable`s.
  minHeight: BLOCK_HEIGHT,
  maxHeight: BLOCK_HEIGHT,
};
const PREDICTED_VS_ACTUAL_KEY = 'Predicted vs. actual';
const MODEL_METRICS_KEY = 'Model metrics';
const MODEL_COMPARISONS_KEY = 'Model class comparisons';
const PARAMETER_IMPORTANCE_KEY = 'Parameter importance';
const PARAMETER_CONTRIBUTION_KEY = 'Parameter contribution';
const PREDICTED_AND_ACTUAL_BY_DATA_POINT_KEY = 'Predicted and actual by data point';
const PARAMETER_CONTRIBUTION_BY_DATA_POINT_KEY = 'Parameter contribution by data point';
const PARAMETERS_BY_DATA_POINT_KEY = 'Parameters by data point';

function ParameterOptimizationModelingDashboard(
  props: MainViewComponentProps<AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_MODELING_DASHBOARD> & {}
): JSX.Element {
  const downstreamSteps = [...getDownstreamSteps(props.analysis, props.step.id)];

  const chartOrTableSteps = downstreamSteps.filter(
    (step) =>
      step.definition.stepType === PipelineStepType.CHART_VIEW ||
      step.definition.stepType === PipelineStepType.TABLE_VIEW
  );
  const filterSteps = downstreamSteps.filter(
    (step) => step.definition.stepType === PipelineStepType.FILTER_DATASET
  );

  const parameterContributionFilterStep = filterSteps[0];
  const [maybeSelectedFeature, setSelectedFeature] = useState(
    getInitialFilterDropdownValue(parameterContributionFilterStep)
  );
  const [isUpdatingSelectedFeature, setIsUpdatingSelectedFeature] = useState(false);

  const dataPointFilterSteps = filterSteps.slice(1);
  const [maybeSelectedDataPointId, setSelectedDataPointId] = useState(
    getInitialFilterDropdownValue(dataPointFilterSteps[1])
  );
  const [isUpdatingSelectedDataPointId, setIsUpdatingSelectedDataPointId] = useState(false);

  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const summarySection = {
    header: 'Model summary',
    blockSpecs: [
      {
        key: PREDICTED_VS_ACTUAL_KEY,
        block: (
          <DashboardVisualizationBlock
            clientStateId={`${props.analysis}-po-dashboard-${chartOrTableSteps[0].id}`}
            isExpanded={expandedKey === PREDICTED_VS_ACTUAL_KEY}
            onToggleExpand={(isExpanded) => {
              setExpandedKey(isExpanded ? PREDICTED_VS_ACTUAL_KEY : null);
            }}
            step={chartOrTableSteps[0]}
            tooltip="The actual experimental output is plotted against the model's predicted output to give you confidence in the model's accuracy."
          />
        ),
      },
      {
        key: MODEL_METRICS_KEY,
        block: (
          <DashboardVisualizationBlock
            clientStateId={`${props.analysis}-po-dashboard-${chartOrTableSteps[1].id}`}
            isExpanded={expandedKey === MODEL_METRICS_KEY}
            onToggleExpand={(isExpanded) => {
              setExpandedKey(isExpanded ? MODEL_METRICS_KEY : null);
            }}
            step={chartOrTableSteps[1]}
            tooltip="R-squared is the percentage of variation that helps you determine how well the model's prediction approximates the actual data. The closer the R-squared value is to 1, the better the fit."
          />
        ),
      },
      {
        ...BASE_BLOCK_SPEC,
        key: MODEL_COMPARISONS_KEY,
        block: (
          <DashboardVisualizationBlock
            clientStateId={`${props.analysis}-po-dashboard-${chartOrTableSteps[2].id}`}
            isExpanded={expandedKey === MODEL_COMPARISONS_KEY}
            onToggleExpand={(isExpanded) => {
              setExpandedKey(isExpanded ? MODEL_COMPARISONS_KEY : null);
            }}
            step={chartOrTableSteps[2]}
            tooltip="This table compares the relative performance of all models trained. Only the best model as measured by Ranking score is used in your analysis."
          />
        ),
      },
    ],
  };

  const parameterAnalysisSection = {
    header: 'Parameter analysis',
    blockSpecs: [
      {
        key: PARAMETER_IMPORTANCE_KEY,
        block: (
          <DashboardVisualizationBlock
            clientStateId={`${props.analysis}-po-dashboard-${chartOrTableSteps[3].id}`}
            isExpanded={expandedKey === PARAMETER_IMPORTANCE_KEY}
            onToggleExpand={(isExpanded) => {
              setExpandedKey(isExpanded ? PARAMETER_IMPORTANCE_KEY : null);
            }}
            step={chartOrTableSteps[3]}
            tooltip="The parameters are ranked from most to least important based on how much they factored into the model's prediction. Importance is always positive."
          />
        ),
      },
      {
        key: PARAMETER_CONTRIBUTION_KEY,
        block: (
          <DashboardVisualizationBlock
            banner={
              <DataFrameFilterDropdown
                disabled={isUpdatingSelectedFeature}
                displayName={
                  maybeSelectedFeature !== UNSET_CONFIG_VARIABLE ? maybeSelectedFeature : 'Select feature'
                }
                filterStep={parameterContributionFilterStep}
                onSelectValue={async (newFilter) => {
                  setSelectedFeature(newFilter.value as string);
                  setIsUpdatingSelectedFeature(true);
                  try {
                    await AnalysisActions.updateAndRunAnalysisStep(props.analysis.id, {
                      stepId: parameterContributionFilterStep.id,
                      stepConfig: {filter: newFilter},
                    });
                  } finally {
                    setIsUpdatingSelectedFeature(false);
                  }
                }}
                selectedValue={maybeSelectedFeature !== UNSET_CONFIG_VARIABLE ? maybeSelectedFeature : null}
              />
            }
            clientStateId={`${props.analysis}-po-dashboard-${chartOrTableSteps[4].id}`}
            isExpanded={expandedKey === PARAMETER_CONTRIBUTION_KEY}
            showLoadingBlock={isUpdatingSelectedFeature}
            onToggleExpand={(isExpanded) => {
              setExpandedKey(isExpanded ? PARAMETER_CONTRIBUTION_KEY : null);
            }}
            step={chartOrTableSteps[4]}
            tooltip="The parameter value is plotted against how much it contributed to the experimental output."
          />
        ),
      },
    ],
  };
  const dataPointDrilldownSection = {
    header: 'Data point drilldown',
    blockSpecs: [
      {
        key: PREDICTED_AND_ACTUAL_BY_DATA_POINT_KEY,
        block: (
          <DashboardVisualizationBlock
            clientStateId={`${props.analysis}-po-dashboard-${chartOrTableSteps[5].id}`}
            isExpanded={expandedKey === PREDICTED_AND_ACTUAL_BY_DATA_POINT_KEY}
            onToggleExpand={(isExpanded) => {
              setExpandedKey(isExpanded ? PREDICTED_AND_ACTUAL_BY_DATA_POINT_KEY : null);
            }}
            step={chartOrTableSteps[5]}
          />
        ),
        spansFullWidth: true,
      },
      {
        key: 'Data point filter dropdown',
        block: (
          <div className={css(styles.dataPointDropdown)}>
            <FieldLabel className="u-small-spacing-right" label="Data point:" />
            <DataFrameFilterDropdown
              className="u-flex-grow"
              disabled={isUpdatingSelectedDataPointId}
              displayName={
                maybeSelectedDataPointId !== UNSET_CONFIG_VARIABLE
                  ? maybeSelectedDataPointId
                  : 'Select data point'
              }
              filterStep={dataPointFilterSteps[1]}
              onSelectValue={async (newFilter) => {
                setSelectedDataPointId(newFilter.value as string);
                setIsUpdatingSelectedDataPointId(true);
                try {
                  await Promise.all(
                    dataPointFilterSteps.map((step) =>
                      AnalysisActions.updateAndRunAnalysisStep(props.analysis.id, {
                        stepId: step.id,
                        stepConfig: {filter: newFilter},
                      })
                    )
                  );
                } finally {
                  setIsUpdatingSelectedDataPointId(false);
                }
              }}
              selectedValue={
                maybeSelectedDataPointId !== UNSET_CONFIG_VARIABLE ? maybeSelectedDataPointId : null
              }
            />
          </div>
        ),
        minHeight: 28,
        spansFullWidth: true,
      },
      {
        ...BASE_BLOCK_SPEC,
        key: PARAMETER_CONTRIBUTION_BY_DATA_POINT_KEY,
        block: (
          <DashboardVisualizationBlock
            clientStateId={`${props.analysis}-po-dashboard-${chartOrTableSteps[6].id}`}
            isExpanded={expandedKey === PARAMETER_CONTRIBUTION_BY_DATA_POINT_KEY}
            showLoadingBlock={isUpdatingSelectedDataPointId}
            onToggleExpand={(isExpanded) => {
              setExpandedKey(isExpanded ? PARAMETER_CONTRIBUTION_BY_DATA_POINT_KEY : null);
            }}
            step={chartOrTableSteps[6]}
            tooltip="The parameter value is plotted against how much it contributed to the experimental output."
          />
        ),
      },
      {
        ...BASE_BLOCK_SPEC,
        key: PARAMETERS_BY_DATA_POINT_KEY,
        block: (
          <DashboardVisualizationBlock
            clientStateId={`${props.analysis}-po-dashboard-${chartOrTableSteps[7].id}`}
            isExpanded={expandedKey === PARAMETERS_BY_DATA_POINT_KEY}
            showLoadingBlock={isUpdatingSelectedDataPointId}
            onToggleExpand={(isExpanded) => {
              setExpandedKey(isExpanded ? PARAMETERS_BY_DATA_POINT_KEY : null);
            }}
            step={chartOrTableSteps[7]}
          />
        ),
      },
    ],
  };

  return (
    <div className={css(styles.wrapper)}>
      <DashboardLayout
        expandedBlockKey={expandedKey}
        sections={[summarySection, parameterAnalysisSection, dataPointDrilldownSection]}
      />
    </div>
  );
}

export default function ParameterOptimizationModelingDashboardView(
  props: MainViewComponentProps<AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_MODELING_DASHBOARD>
): JSX.Element {
  if (
    [...getDownstreamSteps(props.analysis, props.step.id)].some(
      (step) => step.definition.stepType === PipelineStepType.CHART_VIEW
    )
  ) {
    return <ParameterOptimizationModelingDashboard {...props} />;
  } else {
    return <LegacyParameterOptimizationModelingDashboardView {...props} />;
  }
}

const styles = StyleSheet.create({
  wrapper: {
    margin: ANALYSIS_TOOL_SPACING,
  },
  dataPointDropdown: {
    display: 'flex',
    flexDirection: 'row',
    width: 300,
    alignItems: 'center',
  },
});
